import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addDaysIso,
  checkInTime,
  derivedWindows,
  resolveDayPosts,
  staffingConfigFromRows,
  todayIsoParis,
  workloadFromDay,
  type BoardActivity,
  type BoardDay,
  type StaffingConfigSnapshot,
} from '@lemontaigu/staff';
import { useBoard, useStaffingConfig } from '@lemontaigu/staff/react';
import { fmtDayLabel } from './logic.js';

// « Prévisionnel » (Opérations group, operator-only) —
// docs/PREDICTIVE-STAFFING-BRIEF.md §7.1.
//
// FORWARD ONLY, and that boundary is the point (operator, 2026-07-29): « there are
// 2 aspects that should not conflate ». A planned window is an APPROXIMATION and
// everyone knows it — start and end really depend on the load — so this tab shows
// what the load implies for the days ahead, from the staffing profiles. Nothing
// about the past belongs here.
//
// WHY THE « FIDÉLITÉ DU MODÈLE » PANEL WAS REMOVED, one commit after shipping it.
// It compared realised windows against the planned ones over May–July, and the
// operator had already told me those plannings were « a rough estimate », later
// « made up / estimates that I got from rational + my observations ». Measuring
// reality against a baseline known to be invented yields confident numbers about
// nothing. The code is kept (`windowAccuracy`, `windowSamples`) and becomes
// meaningful from the first month this tab has actually forecast — August 2026 —
// when there is a real prediction to be judged against.
//
// STILL MISSING, and not pretended otherwise anywhere in the UI:
//   * a past Gantt of what actually happened (May–July), to let a pattern emerge —
//     the operator's real ask, and no comparison in it;
//   * realised-vs-forecast, from August;
//   * the staffing proposals (WHO fills tomorrow) — schema and service exist, the
//     read adapter does not. Absent rather than half-shown: a proposal list you
//     cannot accept is worse than no list.

const HORIZON_DAYS = 21;

export function Previsionnel() {
  const board = useBoard();
  const staffing = useStaffingConfig();

  const [snapshot, setSnapshot] = useState<StaffingConfigSnapshot | null>(null);
  const [activities, setActivities] = useState<BoardActivity[]>([]);
  const [days, setDays] = useState<BoardDay[]>([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const from = addDaysIso(todayIsoParis(), 1);
  const to = addDaysIso(from, HORIZON_DAYS - 1);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [config, acts, horizon] = await Promise.all([
        staffing.load(),
        board.activities(),
        board.days(from, to),
      ]);
      setSnapshot(config);
      setActivities(acts);
      setDays(horizon);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [staffing, board, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  const specs = useMemo(
    () =>
      snapshot
        ? staffingConfigFromRows({
            profiles: snapshot.profiles,
            bands: snapshot.bands,
            posts: snapshot.posts,
            workContent: snapshot.workContent,
          }).specs
        : [],
    [snapshot],
  );

  const plannedById = useMemo(
    () => new Map(activities.map((a) => [a.id, { start: a.windowStart.slice(0, 5), end: a.windowEnd.slice(0, 5) }])),
    [activities],
  );


  return (
    <div className="planning">
      <div className="equipe-head">
        <h2 className="section-title">Prévisionnel</h2>
        <span className="equipe-hint">
          Les horaires planifiés sont une estimation — ils dépendent de la charge. Ici : uniquement ce que la charge
          implique pour les jours à venir. La comparaison avec le réel n’aura de sens qu’à partir d’août, quand il y
          aura de vraies prévisions à juger.
        </span>
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {loading && <p className="staff-empty">Chargement…</p>}

      {/* ── À VENIR ────────────────────────────────────────────────────── */}
      {!loading && (
        <section className="planning-card">
          <header className="planning-card-head">
            <strong>À venir — {HORIZON_DAYS} jours</strong>
            <span className="equipe-hint">horaires calculés d’après la charge saisie</span>
          </header>

          {specs.length === 0 ? (
            <p className="staff-empty">
              Aucun profil de staffing configuré : rien à calculer. Voir l’onglet <strong>Staffing</strong>.
            </p>
          ) : days.length === 0 ? (
            <p className="staff-empty">
              Aucune charge saisie sur les {HORIZON_DAYS} prochains jours. Les horaires calculés ont besoin des départs,
              recouches et arrivées — saisis dans <strong>Planning</strong>.
            </p>
          ) : (
            days.map((day) => {
              const workload = workloadFromDay(day);
              const readyBy = checkInTime(day.arrivals);
              const windows = derivedWindows({ specs, day, workload, readyBy });
              const posts = resolveDayPosts({ specs, day, workload, readyBy });
              if (posts.length === 0) return null;

              return (
                <div key={day.date} className="planning-list">
                  <header className="planning-team-head">
                    <span>{fmtDayLabel(day.date)}</span>
                    <span className="equipe-hint">
                      {day.departures} départs · {day.stayovers} recouches · {day.arrivals} arrivées · check-in {readyBy}
                    </span>
                  </header>
                  <table className="staff-table">
                    <tbody>
                      {posts.map((post) => {
                        const planned = post.activityId === null ? null : plannedById.get(post.activityId);
                        const derived = post.activityId === null ? undefined : windows.get(post.activityId);
                        return (
                          <tr key={`${post.activityId ?? 'x'}-${post.label}`}>
                            <td>{post.label}</td>
                            <td>
                              {post.heads} pers · {post.windowStart}–{post.windowEnd}
                            </td>
                            <td className="team-card-muted">
                              {derived && planned && (planned.start !== derived.windowStart || planned.end !== derived.windowEnd)
                                ? `planifié ${planned.start}–${planned.end}`
                                : ''}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })
          )}
        </section>
      )}

    </div>
  );
}
